import {
	Box,
	Button,
	Card,
	CardContent,
	Checkbox,
	Chip,
	Collapse,
	Divider,
	FormControlLabel,
	FormGroup,
	Grid,
	Stack,
	Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import AppFieldView from '@/common/components/form-view/AppFieldView';
import { formatAge } from '@/utils/ageHelper';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { DISPLAY_DATE_FORMAT } from '@/utils/dateHelper';
import dayjs from 'dayjs';
import {
	useDeleteAppendix4Mutation,
	useGetAppendix4DetailQuery,
} from '@/api/forms-management/appendix4';
import LogHistory from '../LogHistory';
import FormManagementChip from '@/common/components/FormManagementChip';
import ConfirmationDialog from '@/common/components/ConfirmationDialog/ConfirmationDialog';
import useModal from '@/common/components/ConfirmationDialog/useModal';
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';
import { closeSnackbar, enqueueSnackbar } from 'notistack';
import CloseIcon from '@mui/icons-material/Close';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import { useState } from 'react';

import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
	useGetAppendix4GenerateReportMutation,
	useGetExportCustomerPDFMutation,
} from '@/api/common';

export interface DataGrid {
	updatedAt: string;
	createdAt: string;
}
export default function Appendix4DetailPage() {
	const { t } = useTranslation();
	const { preAdmissionId, appendix4Id, admissionRequestId } = useParams();
	const navigate = useNavigate();
	const location = useLocation();
	const memberListType = location.pathname.includes('admission-request-list')
		? 'admission-request-list'
		: 'member-list';
	const [deleteAppendix4] = useDeleteAppendix4Mutation();
	const showModal = useModal();

	const [getExportCustomerPDF] = useGetExportCustomerPDFMutation();
	const [getAppendix4GenerateReport] = useGetAppendix4GenerateReportMutation();

	const [fromInformationExpanded, setFromInformationExpanded] = useState(true);
	const [toInformationExpanded, setToInformationExpanded] = useState(true);
	const [
		referralICCMWInformationExpanded,
		setReferralICCMWInformationExpanded,
	] = useState(true);
	const [referralInformationExpanded, setReferralInformationExpanded] =
		useState(true);
	const [particularsInformationExpanded, setParticularsInformationExpanded] =
		useState(true);
	const [familyInformationExpanded, setFamilyInformationExpanded] =
		useState(true);
	const [officeInformationExpanded, setOfficeInformationExpanded] =
		useState(true);

	const { data: details }: any = useGetAppendix4DetailQuery(appendix4Id || '', {
		refetchOnMountOrArgChange: true,
	});

	const handleDelete = async () => {
		showModal((props: any) => (
			<ConfirmationDialog
				{...props}
				showCloseIcon={false}
				title={t('preAdmission.deleteConfirmation')}
				titleProps={{ variant: 'h6' }}
				description={
					<Typography variant='body1'>
						{t('preAdmission.isDeleteForm', {
							formName: t('preAdmission.appendix4ReferralForm'),
						})}
					</Typography>
				}
				cancellationText={t('button.cancel')}
				confirmationText={t('button.confirm')}
				confirmationButtonProps={{ type: 'submit', variant: 'contained' }}
				onConfirm={async () => {
					const response = await deleteAppendix4(details?.data.id || '');
					if ('data' in response) {
						enqueueSnackbar(t('snackbar.enquiryRecordDeleteSuccess'), {
							variant: 'warning',
							anchorOrigin: { horizontal: 'center', vertical: 'top' },
							autoHideDuration: 3000,
							action: (key) => {
								return <CloseIcon onClick={() => closeSnackbar(key)} />;
							},
						});
						navigate(
							preAdmissionId
								? `/pre-admission/pre-admission-list/detail/${preAdmissionId}/forms-management`
								: `/member-management/${memberListType}/detail/${admissionRequestId}/forms-management`,
							{ replace: true }
						);
					}
				}}
			/>
		));
	};

	const formartServicesRequiredFromICCMW = () => {
		const arr = [];
		if (details?.data.counselling) arr.push(t('preAdmission.counselling'));
		if (details?.data.groupsProgrammes)
			arr.push(t('preAdmission.groupsAndProgrammes'));
		if (details?.data.skillTraining) arr.push(t('preAdmission.skillTraining'));
		if (details?.data.caseManagement)
			arr.push(t('preAdmission.caseManagement'));
		if (details?.data.carerSupport) arr.push(t('preAdmission.carerSupport'));
		if (details?.data.peerSupportService)
			arr.push(t('preAdmission.peerSupportService'));
		if (details?.data.clinicalPsychologicalService)
			arr.push(t('preAdmission.clinicalPsychologicalService'));
		if (details?.data.childrenServices)
			arr.push(t('preAdmission.servicesForChildrenOfPersonsInMentalRecovery'));
		if (details?.data.othersService)
			arr.push(
				t('preAdmission.others') +
					' (' +
					details?.data.othersServiceSpecify +
					')'
			);

		return arr.join('; ');
	};
	const formartDiagnosisSuspectedMentalHealthProblem = () => {
		const arr = [];

		if (details?.data.diagnosisMentalHealthSpecify) {
			if (details?.data.diagnosisMentalHealth)
				arr.push(
					t('preAdmission.diagnosisMentalHealthProblem') +
						' (' +
						details?.data.diagnosisMentalHealthSpecify +
						')'
				);
			if (details?.data.suspectedMentalHealthProblem)
				arr.push(
					t('preAdmission.suspectedMentalHealthProblem') +
						' (' +
						details?.data.diagnosisMentalHealthSpecify +
						')'
				);
		} else {
			if (details?.data.diagnosisMentalHealth)
				arr.push(t('preAdmission.diagnosisMentalHealthProblem'));
			if (details?.data.suspectedMentalHealthProblem)
				arr.push(t('preAdmission.suspectedMentalHealthProblem'));
		}

		return arr.join(';\n');
	};
	const formartSpecialRemarks = () => {
		const arr = [];

		if (details?.data.conditionalDischarge)
			arr.push(t('preAdmission.conditionalDischarge'));
		if (details?.data.intensiveCare) arr.push(t('preAdmission.intensiveCare'));
		if (details?.data.exIntensiveCare)
			arr.push(t('preAdmission.exIntensiveCare'));
		if (details?.data.specialCare) arr.push(t('preAdmission.specialCare'));
		if (details?.data.conventionalCare)
			arr.push(t('preAdmission.conventionalCare'));

		return arr.join(';\n');
	};
	const formartDetailsOfAnyEmotional = () => {
		const arr = [];

		if (details?.data.suicidalAttempt)
			arr.push(t('preAdmission.suicidalAttempt'));
		if (details?.data.suicidalTendency)
			arr.push(t('preAdmission.suicidalTendency'));
		if (details?.data.violence) arr.push(t('preAdmission.violence'));
		if (details?.data.violenceTendency)
			arr.push(t('preAdmission.violenceTendency'));
		if (details?.data.specialAttentionDetails)
			arr.push(details?.data.specialAttentionDetails);

		return arr.join(';\n');
	};
	const formartRehabilitationServicesWaitlisted = () => {
		const arr = [];

		if (details?.data.supportedEmployment)
			arr.push(t('preAdmission.supportedEmployment'));
		if (details?.data.shelteredWorkshop)
			arr.push(t('preAdmission.shelteredWorkshop'));
		if (details?.data.residentialService)
			arr.push(
				t('preAdmission.residentialService') +
					' (' +
					details?.data.residentialServiceSpecify +
					')'
			);
		if (details?.data.othersRehabilitation)
			arr.push(
				t('preAdmission.others') +
					' (' +
					details?.data.othersRehabilitationSpecify +
					')'
			);

		if (details?.data.notKnown) arr.push(t('preAdmission.notKnown'));

		return arr.join(';\n');
	};
	const formartConsentApplicantText1 = () => {
		const arr = [];

		if (details?.data.receivingObtained)
			arr.push(t('preAdmission.hasBeenObtained'));
		if (details?.data.receivingNotObtained)
			arr.push(t('preAdmission.hasNotBeenObtained'));

		return arr.join(';\n');
	};
	const formartConsentApplicantText2 = () => {
		const arr = [];

		if (details?.data.approachObtained)
			arr.push(t('preAdmission.hasBeenObtained'));
		if (details?.data.approachNotObtained)
			arr.push(t('preAdmission.hasNotBeenObtained'));

		return arr.join(';\n');
	};
	const formartPrefix = () => {
		const arr = [];

		if (details?.data.familyPrefixMr) arr.push(t('preAdmission.mr'));
		if (details?.data.familyPrefixMrs) arr.push(t('preAdmission.mrs'));
		if (details?.data.familyPrefixMs) arr.push(t('preAdmission.ms'));

		return arr.join(';\n');
	};
	const formartThatICCMWProfessionalWorkersCanApproach = () => {
		const arr = [];

		if (details?.data.familyIsHim) arr.push(t('preAdmission.him'));
		if (details?.data.familyIsHer) arr.push(t('preAdmission.her'));

		return arr.join(';\n');
	};
	const formartForReceivingICCMWServices = () => {
		const arr = [];

		if (details?.data.familyConsentObtained)
			arr.push(t('preAdmission.hasBeenObtained'));
		if (details?.data.familyConsentNotObtained)
			arr.push(t('preAdmission.hasNotBeenObtained'));

		return arr.join(';\n');
	};
	const formartRemarks = () => {
		const arr = [];

		if (details?.data.centreWillContinue)
			arr.push(t('preAdmission.remarksText1'));
		if (details?.data.noFollowUpAction)
			arr.push(t('preAdmission.remarksText2'));
		if (details?.data.othersRemarks)
			arr.push(
				t('preAdmission.others') +
					' (' +
					details?.data.othersRemarksSpecify +
					')'
			);

		return arr.join(';\n');
	};

	const formatFamilyLivingWithApplicant = () => {
		const arr = [];

		if (details?.data.familyLivingWithApplicantYes) arr.push(t('Yes'));
		if (details?.data.familyLivingWithApplicantNo) arr.push(t('No'));

		return arr.join(';\n');
	};

	return (
		<>
			<Box sx={{ width: '100%' }}>
				<Box display='flex' justifyContent='space-between'>
					<Typography variant='h4'>
						{t('drawerListItems.appendix4Detail')}
						<FormManagementChip
							sx={{ marginLeft: '10px' }}
							status={details?.data.formStatus}
							label={details?.data.formStatusDesc}
						/>
					</Typography>
					<Box>
						<Button
							endIcon={<FileDownloadOutlinedIcon />}
							onClick={async () => {
								const response = await getAppendix4GenerateReport(
									details?.data.id
								);
								if ('data' in response) {
									getExportCustomerPDF(response?.data?.data.url);
								}
							}}
							variant='contained'
							sx={{ marginRight: '24px' }}
						>
							{t('button.exportAsPdf')}
						</Button>
						<Button
							endIcon={<DeleteOutlinedIcon />}
							onClick={() => handleDelete()}
							variant='contained'
						>
							{t('button.delete')}
						</Button>
					</Box>
				</Box>

				<Card sx={{ padding: '24px 24px', marginTop: 3 }}>
					<Box
						display='flex'
						justifyContent='space-between'
						marginBottom={2}
						onClick={() => setFromInformationExpanded(!fromInformationExpanded)}
					>
						<Typography variant='body1' sx={{ fontWeight: 'bold' }}>
							{t('preAdmission.from')}
						</Typography>
						<ExpandMoreIcon
							sx={{
								transform: fromInformationExpanded ? 'rotate(180deg)' : 'none',
							}}
						/>
					</Box>
					<Collapse in={fromInformationExpanded}>
						<Grid container spacing={3} marginTop={0}>
							<Grid size={6}>
								<AppFieldView
									label={t('preAdmission.officerInCharge')}
									value={details?.data.fromOfficerInCharge}
								/>
							</Grid>
							<Grid size={6}>
								<AppFieldView
									label={t('preAdmission.unit')}
									value={details?.data.branchName}
								/>
							</Grid>
							<Grid size={6}>
								<AppFieldView
									label={t('preAdmission.ref')}
									value={details?.data.fromRefNo}
								/>
							</Grid>
							<Grid size={6}>
								<AppFieldView
									label={t('preAdmission.telNo')}
									value={details?.data.fromTelNo}
								/>
							</Grid>
							<Grid size={6}>
								<AppFieldView
									label={t('preAdmission.faxNo')}
									value={details?.data.fromFaxNo}
								/>
							</Grid>
							<Grid size={6}>
								<AppFieldView
									label={t('preAdmission.date')}
									value={
										details?.data.fromDate
											? dayjs(details?.data.fromDate)
													.locale('en')
													.format(DISPLAY_DATE_FORMAT)
											: ''
									}
								/>
							</Grid>
						</Grid>
					</Collapse>

					<Divider sx={{ margin: '24px 0' }} />
					<Box
						display='flex'
						justifyContent='space-between'
						marginBottom={2}
						onClick={() => setToInformationExpanded(!toInformationExpanded)}
					>
						<Typography variant='body1' sx={{ fontWeight: 'bold' }}>
							{t('preAdmission.to')}
						</Typography>
						<ExpandMoreIcon
							sx={{
								transform: toInformationExpanded ? 'rotate(180deg)' : 'none',
							}}
						/>
					</Box>
					<Collapse in={toInformationExpanded}>
						<Grid container spacing={3} marginTop={0}>
							<Grid size={6}>
								<AppFieldView
									label={t('preAdmission.officerInCharge')}
									value={details?.data.toOfficerInCharge}
								/>
							</Grid>
							<Grid size={6}>
								<AppFieldView
									label={t('preAdmission.nameOfTheOrganisation')}
									value={details?.data.nameOfOrganization}
								/>
							</Grid>
							<Grid size={6}>
								<AppFieldView
									label={t('preAdmission.ref')}
									value={details?.data.toRefNo}
								/>
							</Grid>
							<Grid size={6}>
								<AppFieldView
									label={t('preAdmission.dated')}
									value={
										details?.data.toDated
											? dayjs(details?.data.toDated)
													.locale('en')
													.format(DISPLAY_DATE_FORMAT)
											: ''
									}
								/>
							</Grid>
							<Grid size={6}>
								<AppFieldView
									label={t('preAdmission.faxNo')}
									value={details?.data.toFaxNo}
								/>
							</Grid>
							<Grid size={6}>
								<AppFieldView
									label={t('preAdmission.totalPages')}
									value={details?.data.toTotalPages}
								/>
							</Grid>
						</Grid>
					</Collapse>

					<Divider sx={{ margin: '24px 0' }} />
					<Box
						display='flex'
						justifyContent='space-between'
						marginBottom={2}
						onClick={() =>
							setReferralICCMWInformationExpanded(
								!referralICCMWInformationExpanded
							)
						}
					>
						<Typography variant='body1' sx={{ fontWeight: 'bold' }}>
							{t('preAdmission.referralForICCMW')}
						</Typography>
						<ExpandMoreIcon
							sx={{
								transform: referralICCMWInformationExpanded
									? 'rotate(180deg)'
									: 'none',
							}}
						/>
					</Box>
					<Collapse in={referralICCMWInformationExpanded}>
						<Grid container spacing={3} marginTop={0}>
							<Grid size={6}>
								<AppFieldView
									label={t('preAdmission.name_en')}
									value={details?.data.nameEn}
								/>
							</Grid>
							<Grid size={6}>
								<AppFieldView
									label={t('preAdmission.sex')}
									value={details?.data.sexDesc}
								/>
							</Grid>
							<Grid size={6}>
								<AppFieldView
									label={t('preAdmission.age')}
									value={formatAge(
										details?.data.age,
										details?.data.ageEstimated
									)}
								/>
							</Grid>
							<Grid size={6}>
								<AppFieldView
									label={t('preAdmission.dateOfBirth')}
									value={
										details?.data.dateOfBirth
											? dayjs(details?.data.dateOfBirth)
													.locale('en')
													.format(DISPLAY_DATE_FORMAT)
											: ''
									}
								/>
							</Grid>
							<Grid size={6}>
								<AppFieldView
									label={t('preAdmission.address')}
									value={details?.data.fullAddress}
								/>
							</Grid>
						</Grid>
					</Collapse>

					<Divider sx={{ margin: '24px 0' }} />
					<Box
						display='flex'
						justifyContent='space-between'
						marginBottom={2}
						onClick={() =>
							setReferralInformationExpanded(!referralInformationExpanded)
						}
					>
						<Typography variant='body1' sx={{ fontWeight: 'bold' }}>
							{t('preAdmission.referrerInformation')}
						</Typography>
						<ExpandMoreIcon
							sx={{
								transform: referralInformationExpanded
									? 'rotate(180deg)'
									: 'none',
							}}
						/>
					</Box>
					<Collapse in={referralInformationExpanded}>
						<Grid container spacing={3} marginTop={0}>
							<Grid size={6}>
								<AppFieldView
									label={t('preAdmission.nameOfReferrer')}
									value={details?.data.nameOfReferrer}
								/>
							</Grid>
							<Grid size={6}>
								<AppFieldView
									label={t('preAdmission.nameOfICCMWWorker')}
									value={details?.data.iccmwWorkerName}
								/>
							</Grid>
							<Grid size={12}>
								<AppFieldView
									label={t('preAdmission.discussionDate')}
									value={
										details?.data.discussionDate
											? dayjs(details?.data.discussionDate)
													.locale('en')
													.format(DISPLAY_DATE_FORMAT)
											: ''
									}
								/>
							</Grid>
							<Grid size={12}>
								<Typography variant='body2' sx={{ fontWeight: 'bold' }}>
									{t('preAdmission.wouldLikeToRefer')}
								</Typography>
								<Grid container spacing={3}>
									<Grid size={6}>
										<AppFieldView
											label={`${t('preAdmission.his')} / ${t('preAdmission.her')}`}
											value={
												details?.data.referralIsHis &&
												details?.data.referralIsHer
													? `${t('preAdmission.his')} / ${t('preAdmission.her')}`
													: details?.data.referralIsHis
														? t('preAdmission.his')
														: details?.data.referralIsHer
															? t('preAdmission.her')
															: ''
											}
										/>
									</Grid>
									<Grid size={6}>
										<AppFieldView
											label={`${t('preAdmission.mentalHealth')} / ${t(
												'preAdmission.suspectedMentalHealthProblems'
											)}`}
											value={
												details?.data.mentalHealth &&
												details?.data.suspectedMentalHealth
													? `${t('preAdmission.mentalHealth')}/${t(
															'preAdmission.suspectedMentalHealthProblems'
														)}`
													: details?.data.mentalHealth
														? t('preAdmission.mentalHealth')
														: details?.data.suspectedMentalHealth
															? t('preAdmission.suspectedMentalHealthProblems')
															: ''
											}
										/>
									</Grid>
								</Grid>
							</Grid>
						</Grid>
					</Collapse>

					<Divider sx={{ margin: '24px 0' }} />
					<Box
						display='flex'
						justifyContent='space-between'
						marginBottom={2}
						onClick={() =>
							setParticularsInformationExpanded(!particularsInformationExpanded)
						}
					>
						<Typography variant='body1' sx={{ fontWeight: 'bold' }}>
							{t('preAdmission.particularsOfApplicant')}
						</Typography>
						<ExpandMoreIcon
							sx={{
								transform: particularsInformationExpanded
									? 'rotate(180deg)'
									: 'none',
							}}
						/>
					</Box>
					<Collapse in={particularsInformationExpanded}>
						<Grid container spacing={3} marginTop={0}>
							<Grid size={6}>
								<AppFieldView
									label={t('preAdmission.name_en')}
									value={details?.data.applicantNameEn}
								/>
							</Grid>
							<Grid size={6}>
								<AppFieldView
									label={t('preAdmission.name_zh')}
									value={details?.data.applicantNameZh}
								/>
							</Grid>
							<Grid size={6}>
								<AppFieldView
									label={t('preAdmission.telNoHome')}
									value={details?.data.homeTel}
								/>
							</Grid>
							<Grid size={6}>
								<AppFieldView
									label={t('preAdmission.telNoMobile')}
									value={details?.data.mobileTel}
								/>
							</Grid>
							<Grid size={6}>
								<AppFieldView
									label={t('preAdmission.hkicNo')}
									value={details?.data.hkicNo}
								/>
							</Grid>
						</Grid>
						<Divider sx={{ margin: '24px 0' }} />
						<Grid container spacing={3} marginTop={0}>
							<Grid size={12}>
								<AppFieldView
									label={t('preAdmission.servicesRequiredFromICCMW')}
									value={formartServicesRequiredFromICCMW()}
								/>
							</Grid>
							<Grid size={12}>
								<AppFieldView
									label={t(
										'preAdmission.diagnosisSuspectedMentalHealthProblem'
									)}
									value={formartDiagnosisSuspectedMentalHealthProblem()}
								/>
							</Grid>
							<Grid size={6}>
								<AppFieldView
									label={t('preAdmission.dateOfOnsetIfAny')}
									value={details?.data.dateOfOnset}
								/>
							</Grid>
							<Grid size={6}>
								<AppFieldView
									label={t('preAdmission.psychiatricFollowUpClinicIfAny')}
									value={details?.data.psychiatricFollowUpClinic}
								/>
							</Grid>
							<Grid size={6}>
								<AppFieldView
									label={t('preAdmission.specialRemarks')}
									value={formartSpecialRemarks()}
								/>
							</Grid>
						</Grid>
						<Divider sx={{ margin: '24px 0' }} />
						<Grid container spacing={3} marginTop={0}>
							<Grid size={12}>
								<Typography variant='body1' sx={{ fontWeight: 'bold' }}>
									{t('preAdmission.contactPointsOfCaseManagerOf')}
								</Typography>
							</Grid>
							<Grid size={12}>
								<Box display={'flex'}>
									{/* <FormGroup> */}
									<FormControlLabel
										disabled
										control={<Checkbox checked={!!details?.data.cps} />}
										label={t('preAdmission.cps')}
									/>
									<FormControlLabel
										disabled
										control={<Checkbox checked={!!details?.data.pcp} />}
										label={t('preAdmission.pcp')}
									/>
									{/* </FormGroup> */}
								</Box>
							</Grid>
							<Grid size={6}>
								<AppFieldView
									label={t('preAdmission.name_en')}
									value={details?.data.caseManagerName}
								/>
							</Grid>
							<Grid size={6}>
								<AppFieldView
									label={t('preAdmission.telNo')}
									value={details?.data.caseManagerTel}
								/>
							</Grid>
							<Grid size={6}>
								<AppFieldView
									label={t('preAdmission.otherSupportServicesEg')}
									value={details?.data.otherSupportServices}
								/>
							</Grid>
						</Grid>
						<Divider sx={{ margin: '24px 0' }} />
						<Grid container spacing={3} marginTop={0}>
							<Grid size={12}>
								<AppFieldView
									label={t('preAdmission.detailsOfAnyEmotional')}
									value={formartDetailsOfAnyEmotional()}
								/>
							</Grid>
						</Grid>
						<Divider sx={{ margin: '24px 0' }} />
						<Grid container spacing={3} marginTop={0}>
							<Grid size={12}>
								<AppFieldView
									label={t('preAdmission.rehabilitationServicesWaitlisted')}
									value={formartRehabilitationServicesWaitlisted()}
								/>
							</Grid>
						</Grid>
						<Divider sx={{ margin: '24px 0' }} />
						<Grid container spacing={3} marginTop={0}>
							<Grid size={12}>
								<Typography variant='body2' sx={{ fontWeight: 'bold' }}>
									{t('preAdmission.consentOfApplicant')}
								</Typography>
							</Grid>
							<Grid size={12}>
								<AppFieldView
									label={t('preAdmission.consentApplicantText1')}
									value={formartConsentApplicantText1()}
								/>
							</Grid>
							<Grid size={12}>
								<AppFieldView
									label={t('preAdmission.consentApplicantText2')}
									value={formartConsentApplicantText2()}
								/>
							</Grid>
						</Grid>
					</Collapse>

					<Divider sx={{ margin: '24px 0' }} />
					<Box
						display='flex'
						justifyContent='space-between'
						marginBottom={2}
						onClick={() =>
							setFamilyInformationExpanded(!familyInformationExpanded)
						}
					>
						<Typography variant='body1' sx={{ fontWeight: 'bold' }}>
							{t('preAdmission.informationOfApplicantFamilyMemberCarer')}
						</Typography>
						<ExpandMoreIcon
							sx={{
								transform: familyInformationExpanded
									? 'rotate(180deg)'
									: 'none',
							}}
						/>
					</Box>
					<Collapse in={familyInformationExpanded}>
						<Grid container spacing={3} marginTop={0}>
							<Grid size={12}>
								<AppFieldView
									label={t('preAdmission.prefix')}
									value={formartPrefix()}
								/>
							</Grid>
							<Grid size={6}>
								<AppFieldView
									label={t('preAdmission.name_en')}
									value={details?.data.familyNameEn}
								/>
							</Grid>
							<Grid size={6}>
								<AppFieldView
									label={t('preAdmission.name_zh')}
									value={details?.data.familyNameCh}
								/>
							</Grid>
							<Grid size={6}>
								<AppFieldView
									label={t('preAdmission.telNo')}
									value={details?.data.familyTel}
								/>
							</Grid>
							<Grid size={6}>
								<AppFieldView
									label={t('preAdmission.livingWithTheApplicant')}
									value={formatFamilyLivingWithApplicant()}
								/>
							</Grid>
							<Grid size={6}>
								<AppFieldView
									label={t('preAdmission.relationshipWithApplicant')}
									value={details?.data.relationshipWithApplicantDesc}
								/>
							</Grid>
						</Grid>
						<Divider sx={{ margin: '24px 0' }} />
						<Typography variant='body2' sx={{ fontWeight: 'bold' }}>
							{t('preAdmission.consentOfFamilyMemberCarer')}
						</Typography>
						<Grid container spacing={3} marginTop={0}>
							<Grid size={6}>
								<AppFieldView
									label={t(
										'preAdmission.thatICCMWProfessionalWorkersCanApproach'
									)}
									value={formartThatICCMWProfessionalWorkersCanApproach()}
								/>
							</Grid>
							<Grid size={6}>
								<AppFieldView
									label={t('preAdmission.forReceivingICCMWServices')}
									value={formartForReceivingICCMWServices()}
								/>
							</Grid>
						</Grid>
					</Collapse>

					<Divider sx={{ margin: '24px 0' }} />
					<Box display='flex' justifyContent='space-between' marginBottom={2}>
						<Typography variant='body1' sx={{ fontWeight: 'bold' }}>
							{t('preAdmission.referralSummaryAndSpecialRemarks')}
						</Typography>
					</Box>
					<Grid container spacing={3} marginTop={0}>
						<Grid size={12}>
							<AppFieldView
								label={t('preAdmission.referralSummaryAndSpecialRemarksInput')}
								value={details?.data.referralSummary}
							/>
						</Grid>
					</Grid>

					<Divider sx={{ margin: '24px 0' }} />
					<Box
						display='flex'
						justifyContent='space-between'
						marginBottom={2}
						onClick={() =>
							setOfficeInformationExpanded(!officeInformationExpanded)
						}
					>
						<Typography variant='body1' sx={{ fontWeight: 'bold' }}>
							{t('preAdmission.informationOfReferringOffice')}
						</Typography>
						<ExpandMoreIcon
							sx={{
								transform: officeInformationExpanded
									? 'rotate(180deg)'
									: 'none',
							}}
						/>
					</Box>
					<Collapse in={officeInformationExpanded}>
						<Grid container spacing={3} marginTop={0}>
							<Grid size={6}>
								<AppFieldView
									label={t('preAdmission.nameOfReferrer')}
									value={details?.data.referringName}
								/>
							</Grid>
							<Grid size={6}>
								<AppFieldView
									label={t('preAdmission.post')}
									value={details?.data.referringPost}
								/>
							</Grid>
							<Grid size={6}>
								<AppFieldView
									label={t('preAdmission.telNo')}
									value={details?.data.referringTel}
								/>
							</Grid>
							<Grid size={6}>
								<AppFieldView
									label={t('preAdmission.agency')}
									value={details?.data.referringAgency}
								/>
							</Grid>
							<Grid size={6}>
								<AppFieldView
									label={t('preAdmission.faxNo')}
									value={details?.data.referringFax}
								/>
							</Grid>
							<Grid size={6}>
								<AppFieldView
									label={t('preAdmission.officeAddress')}
									value={details?.data.referringOfficeAddress}
								/>
							</Grid>
							<Grid size={12}>
								<AppFieldView
									label={t('preAdmission.remarks')}
									value={formartRemarks()}
								/>
							</Grid>

							<Grid size={12}>
								<Typography variant='body2' sx={{ fontWeight: 'bold' }}>
									{t('preAdmission.remarksTittle')}
								</Typography>
							</Grid>

							<Grid size={6}>
								<AppFieldView
									label={t('preAdmission.name')}
									value={details?.data.contactWorkerName}
								/>
							</Grid>
							<Grid size={6}>
								<AppFieldView
									label={t('preAdmission.telNo')}
									value={details?.data.contactTel}
								/>
							</Grid>
							<Grid size={6}>
								<AppFieldView
									label={t('preAdmission.officerInCharge')}
									value={details?.data.contactOfficerInCharge}
								/>
							</Grid>
						</Grid>
					</Collapse>
				</Card>

				{details?.data.id && (
					<Card sx={{ margin: '24px 0' }}>
						<CardContent>
							<LogHistory formId={details.data.id} />
						</CardContent>
					</Card>
				)}
			</Box>
		</>
	);
}
